/* Display-only foil and impressed surfaces. No catalogue or manufacturing rules. */
(function () {
  'use strict';
  const registry = window.TodayMerchPreviewRenderers = window.TodayMerchPreviewRenderers || {};
  if (registry.surfaceFinish) return;
  const metadata = new WeakMap();
  const limits = Object.freeze({ maxSide: 4096, maxPixels: 4194304, chunkRows: 32 });
  const kinds = Object.freeze(['foil', 'deboss', 'emboss']);
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const number = (value, fallback, min, max) => typeof value === 'number' && Number.isFinite(value) ? clamp(value, min, max) : fallback;
  const check = signal => { if (signal?.aborted) throw new DOMException('Surface preview superseded', 'AbortError'); };
  const pause = () => new Promise(resolve => window.setTimeout(resolve, 0));
  function color(value) {
    if (typeof value !== 'string' || !/^#[a-f\d]{3}(?:[a-f\d]{3})?$/i.test(value)) return null;
    let hex = value.slice(1);
    if (hex.length === 3) hex = hex.split('').map(x => x + x).join('');
    return [0, 2, 4].map(i => parseInt(hex.slice(i, i + 2), 16));
  }
  function inspect(options = {}) {
    options = options && typeof options === 'object' ? options : {};
    const profile = options.surfaceFinish;
    const base = { previewOnly: true, applied: false, kind: null, colorTreatment: 'original', reason: null };
    const fallback = reason => Object.freeze({ ...base, reason, limitations: Object.freeze(['Rendu de finition non qualifié : fichier affiché à plat.']) });
    if (options.qualified === false || options.qualification?.compatible === false
      || (options.qualification?.status && options.qualification.status !== 'allowed')) return fallback('qualification-refused');
    if (options.qualified !== true && !(options.qualification?.status === 'allowed' && options.qualification?.compatible === true)) return fallback('qualification-required');
    if (!profile || profile.qualified !== true || !kinds.includes(profile.kind)) return fallback('finish-profile-required');
    if (typeof options.techniqueId !== 'string' || !options.techniqueId.trim() || options.techniqueId !== profile.techniqueId) return fallback('technique-profile-mismatch');
    if (typeof profile.source !== 'string' || !profile.source.trim() || typeof profile.revision !== 'string' || !profile.revision.trim()) return fallback('profile-provenance-required');
    if (profile.maskQualified !== true) return fallback('alpha-mask-required');
    if (!color(profile.color)) return fallback('finish-color-required');
    return Object.freeze({ ...base, applied: true, kind: profile.kind, reason: 'simulation', colorTreatment: 'finish-monochrome',
      profileSource: profile.source, profileRevision: profile.revision,
      limitations: Object.freeze([
        profile.kind === 'foil' ? 'Reflets illustratifs ; film et couleur réels à confirmer.' : 'Ombre de creux ou relief illustrative ; profondeur et texture réelles non simulées.',
        'Le fichier effectif conserve ses couleurs. L’aperçu monochrome ne remplace pas ce fichier.',
        'Cet aperçu ne valide ni les détails fabricables ni la production.'
      ]) });
  }
  async function render(source, options = {}) {
    options = options && typeof options === 'object' ? options : {};
    const signal = options.signal; check(signal);
    const view = source?.ownerDocument?.defaultView;
    if (!view || !(source instanceof view.HTMLCanvasElement)) throw new TypeError('A source canvas is required.');
    const width = source.width, height = source.height;
    if (!width || !height || width > limits.maxSide || height > limits.maxSide || width * height > limits.maxPixels) throw new RangeError('Surface preview exceeds canvas bounds.');
    const output = source.ownerDocument.createElement('canvas'); output.width = width; output.height = height;
    const context = output.getContext('2d', { willReadFrequently: true }); context.drawImage(source, 0, 0);
    let info = inspect(options);
    const strength = number(options.strength, 0.7, 0, 1);
    if (!info.applied || strength === 0) {
      if (strength === 0) info = Object.freeze({ ...info, applied: false, reason: 'disabled', colorTreatment: 'original' });
      metadata.set(output, info); return output;
    }
    // Capture the entire job before yielding: later source or option edits are irrelevant.
    const rgb = color(options.surfaceFinish.color);
    const image = context.getImageData(0, 0, width, height), pixels = image.data;
    const alpha = new Uint8Array(width * height);
    for (let i = 0; i < alpha.length; i++) alpha[i] = pixels[i * 4 + 3];
    const direction = options.lighting?.direction;
    let dx = number(direction?.[0], -0.6, -1, 1), dy = number(direction?.[1], -0.8, -1, 1);
    const length = Math.hypot(dx, dy); if (length) { dx /= length; dy /= length; }
    // The edge width is in preview pixels, never converted to physical relief depth.
    const edge = Math.max(1, Math.min(3, Math.round(Math.min(width, height) / 180)));
    const alphaAt = (x, y) => x < 0 || y < 0 || x >= width || y >= height ? 0 : alpha[y * width + x] / 255;
    await pause(); check(signal);
    let occupied = 0;
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const i = (y * width + x) * 4;
        if (!pixels[i + 3]) continue;
        occupied++;
        const nx = alphaAt(x - edge, y) - alphaAt(x + edge, y);
        const ny = alphaAt(x, y - edge) - alphaAt(x, y + edge);
        const contourLight = nx * dx + ny * dy;
        let shade;
        if (info.kind === 'foil') {
          const position = (x / Math.max(1, width - 1) - 0.5) * dx + (y / Math.max(1, height - 1) - 0.5) * dy;
          const band = Math.exp(-(((position - 0.12) / 0.095) ** 2));
          const broad = Math.cos(position * Math.PI * 2.2);
          shade = strength * (0.21 * broad + 0.29 * band - 0.17 + 0.14 * contourLight);
        } else {
          const sign = info.kind === 'emboss' ? 1 : -1;
          shade = strength * (sign * 0.34 * contourLight + sign * 0.015);
        }
        for (let channel = 0; channel < 3; channel++) {
          // RGB comes from the qualified finish, alpha exclusively from the effective design.
          pixels[i + channel] = clamp(Math.round(shade >= 0 ? rgb[channel] + (255 - rgb[channel]) * shade : rgb[channel] * (1 + shade)), 0, 255);
        }
      }
      if ((y + 1) % limits.chunkRows === 0 && y + 1 < height) { await pause(); check(signal); }
    }
    check(signal); context.putImageData(image, 0, 0);
    metadata.set(output, occupied ? info : Object.freeze({ ...info, applied: false, reason: 'empty-artwork', colorTreatment: 'original' }));
    return output;
  }
  registry.surfaceFinish = Object.freeze({ render, inspect, getMetadata: canvas => metadata.get(canvas) || null, kinds, limits, version: '1.0.0' });
})();
